'use client';

/**
 * StepIndicator Component
 *
 * Visual step indicator for the FormWizard component.
 * Shows numbered circles with connecting lines between steps.
 */

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { StepStatus } from '@ops-matrix/shared-types';
import { motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { baseColors, getNeonStyles, type NeonAccent, neonAccents } from '../../theme/palette';
import { createNeonGlow } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

// StepStatus enum is now imported from @ops-matrix/shared-types

export interface StepIndicatorStep {
  id: string;
  title: string;
  description?: string;
  optional?: boolean;
  /**
   * Step status for scroll mode (overrides position-based status)
   */
  status?: StepStatus;
}

export interface StepIndicatorProps {
  /**
   * Array of steps to display
   */
  steps: StepIndicatorStep[];

  /**
   * Current active step (0-indexed)
   */
  currentStep: number;

  /**
   * Callback when a step is clicked
   */
  onStepClick?: (stepIndex: number) => void;

  /**
   * Whether clicking on steps is allowed
   * @default true
   */
  clickable?: boolean;

  /**
   * Neon accent color
   * @default 'cyan'
   */
  neonAccent?: NeonAccent;

  /**
   * Size variant
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Orientation
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';

  /**
   * Use status-based coloring (scroll mode)
   * When true, uses step.status instead of position-based completed/active
   * @default false
   */
  useStatusMode?: boolean;

  /**
   * Additional class name
   */
  className?: string;
}

// Status colors for scroll mode (using StepStatus enum)
// Note: CURRENT uses 'primary' as a flag to use theme.palette.primary.main
const STATUS_COLORS = {
  [StepStatus.COMPLETED]: baseColors.success[500],
  [StepStatus.PENDING]: baseColors.neutral[400],
  [StepStatus.SKIPPED]: baseColors.neutral[400],
  [StepStatus.ERROR]: baseColors.error[400],
  [StepStatus.CURRENT]: 'primary', // Uses theme primary color
} as const;

// ============================================================================
// Size Configurations
// ============================================================================

const getSizeConfig = (size: 'small' | 'medium' | 'large') => {
  const configs = {
    small: {
      circle: 28,
      fontSize: '0.75rem',
      lineHeight: 2,
      titleSize: '0.75rem',
      descSize: '0.6875rem',
      gap: 4,
    },
    medium: {
      circle: 36,
      fontSize: '0.875rem',
      lineHeight: 2,
      titleSize: '0.875rem',
      descSize: '0.8125rem',
      gap: 6,
    },
    large: {
      circle: 44,
      fontSize: '1rem',
      lineHeight: 3,
      titleSize: '1rem',
      descSize: '0.875rem',
      gap: 8,
    },
  };
  return configs[size];
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledStepCircleProps {
  $isActive: boolean;
  $isCompleted: boolean;
  $isClickable: boolean;
  $size: number;
  $neonAccent: NeonAccent;
  $isDark: boolean;
  $status?: StepStatus;
  $useStatusMode?: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
  $orientation?: 'horizontal' | 'vertical';
  $lineWidth?: number;
}

const StyledStepCircle = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledStepCircleProps>(
  ({
    theme,
    $isActive,
    $isCompleted,
    $isClickable,
    $size,
    $neonAccent,
    $isDark,
    $status,
    $useStatusMode,
    $isFirst,
    $isLast,
    $orientation,
    $lineWidth = 2,
  }) => {
    const neonStyles = getNeonStyles($neonAccent, $isDark);
    // Use theme color for 'primary' accent to respect theme overrides (e.g., Consumer Portal green)
    const baseColor = $neonAccent === 'primary' ? theme.palette.primary.main : neonAccents[$neonAccent].base;
    const isVertical = $orientation === 'vertical';

    // Status mode uses explicit status colors
    if ($useStatusMode && $status) {
      // CURRENT uses theme primary, others use STATUS_COLORS
      const statusColor = $status === StepStatus.CURRENT ? theme.palette.primary.main : STATUS_COLORS[$status];
      const lineColor =
        $status === StepStatus.COMPLETED || $status === StepStatus.CURRENT
          ? $status === StepStatus.CURRENT
            ? theme.palette.primary.main
            : STATUS_COLORS[StepStatus.COMPLETED]
          : $isDark
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.08)';

      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: $size,
        height: $size,
        borderRadius: '50%',
        fontSize: 'inherit',
        fontWeight: 600,
        cursor: $isClickable ? 'pointer' : 'default',
        transition: transitionPresets.button,
        position: 'relative',
        zIndex: 1,
        flexShrink: 0,

        // Status-based styling
        ...($status === StepStatus.COMPLETED && {
          background: statusColor,
          border: `2px solid ${statusColor}`,
          color: baseColors.neutral[0],
          boxShadow: `0 0 12px ${statusColor}40`,
        }),

        ...($status === StepStatus.CURRENT && {
          background: `${statusColor}20`,
          border: `2px solid ${statusColor}`,
          color: statusColor,
          boxShadow: `0 0 16px ${statusColor}50`,
        }),

        ...($status === StepStatus.PENDING && {
          background: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          border: `2px solid ${statusColor}`,
          color: statusColor,
        }),

        ...($status === StepStatus.ERROR && {
          background: `${statusColor}20`,
          border: `2px solid ${statusColor}`,
          color: statusColor,
          boxShadow: `0 0 12px ${statusColor}40`,
        }),

        // Connecting line extending UP (vertical only, not first)
        ...(!$isFirst &&
          isVertical && {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              bottom: '100%',
              width: $lineWidth,
              height: 16,
              background: lineColor,
              transform: 'translateX(-50%)',
              zIndex: 0,
            },
          }),

        // Connecting line extending DOWN (vertical only, not last)
        ...(!$isLast &&
          isVertical && {
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: '100%',
              width: $lineWidth,
              height: 16,
              background: lineColor,
              transform: 'translateX(-50%)',
              zIndex: 0,
            },
          }),

        // Hover effects
        '&:hover': {
          ...($isClickable && {
            transform: 'scale(1.1)',
            boxShadow: `0 0 20px ${statusColor}60`,
          }),
        },
      };
    }

    // Default position-based mode (original behavior)
    const lineColor = $isCompleted ? baseColor : $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: $size,
      height: $size,
      borderRadius: '50%',
      fontSize: 'inherit',
      fontWeight: 600,
      cursor: $isClickable ? 'pointer' : 'default',
      transition: transitionPresets.button,
      position: 'relative',
      zIndex: 1,
      flexShrink: 0,

      // State-based styling
      ...($isCompleted && {
        background: baseColor,
        border: `2px solid ${baseColor}`,
        color: $neonAccent === 'primary' ? theme.palette.primary.contrastText : baseColors.neutral[0],
        boxShadow: createNeonGlow($neonAccent, 'subtle'),
      }),

      ...($isActive &&
        !$isCompleted && {
          background: neonStyles.background,
          border: `2px solid ${baseColor}`,
          color: baseColor,
          boxShadow: createNeonGlow($neonAccent, 'medium'),
        }),

      ...(!$isActive &&
        !$isCompleted && {
          background: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
          border: `2px solid ${$isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.15)'}`,
          color: $isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
        }),

      // Connecting line extending UP (vertical only, not first)
      ...(!$isFirst &&
        isVertical && {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '50%',
            bottom: '100%',
            width: $lineWidth,
            height: 16,
            background: lineColor,
            transform: 'translateX(-50%)',
            zIndex: 0,
          },
        }),

      // Connecting line extending DOWN (vertical only, not last)
      ...(!$isLast &&
        isVertical && {
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '50%',
            top: '100%',
            width: $lineWidth,
            height: 16,
            background: lineColor,
            transform: 'translateX(-50%)',
            zIndex: 0,
          },
        }),

      // Hover effects
      '&:hover': {
        ...($isClickable && {
          transform: 'scale(1.1)',
          boxShadow: createNeonGlow($neonAccent, $isActive || $isCompleted ? 'strong' : 'subtle'),
        }),
      },
    };
  },
);

interface StyledConnectorProps {
  $isCompleted: boolean;
  $neonAccent: NeonAccent;
  $isDark: boolean;
  $orientation: 'horizontal' | 'vertical';
  $lineHeight: number;
  $circleSize: number;
  $useStatusMode?: boolean;
  $fromStatus?: StepStatus;
}

const StyledConnector = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledConnectorProps>(
  ({
    theme,
    $isCompleted,
    $neonAccent,
    $isDark,
    $orientation,
    $lineHeight,
    $circleSize,
    $useStatusMode,
    $fromStatus,
  }) => {
    // Use theme color for 'primary' accent to respect theme overrides
    const baseColor = $neonAccent === 'primary' ? theme.palette.primary.main : neonAccents[$neonAccent].base;
    const isHorizontal = $orientation === 'horizontal';

    // In status mode, use the step's status color for the connector
    // CURRENT uses theme primary, COMPLETED uses green
    const connectorColor =
      $useStatusMode && $fromStatus
        ? $fromStatus === StepStatus.CURRENT
          ? theme.palette.primary.main
          : STATUS_COLORS[$fromStatus]
        : baseColor;
    const showProgress = $useStatusMode
      ? $fromStatus === StepStatus.COMPLETED || $fromStatus === StepStatus.CURRENT
      : $isCompleted;

    // Calculate center position for vertical orientation (circle center minus border)
    const centerOffset = $circleSize / 2 - 1;

    return {
      position: 'relative',
      flex: isHorizontal ? 1 : undefined,
      height: isHorizontal ? $lineHeight : 32,
      width: isHorizontal ? undefined : $lineHeight,
      margin: isHorizontal ? `0 ${$lineHeight * 2}px` : `-${$lineHeight}px 0`, // Negative margin to overlap circles
      marginLeft: isHorizontal ? undefined : centerOffset, // Dynamic center alignment

      // Track (background line)
      '&::before': {
        content: '""',
        position: 'absolute',
        ...(isHorizontal
          ? {
              top: '50%',
              left: 0,
              right: 0,
              height: $lineHeight,
              transform: 'translateY(-50%)',
            }
          : {
              left: '50%',
              top: 0,
              bottom: 0,
              width: $lineHeight,
              transform: 'translateX(-50%)',
            }),
        background: $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
        borderRadius: $lineHeight / 2,
      },

      // Progress (foreground line)
      '&::after': {
        content: '""',
        position: 'absolute',
        ...(isHorizontal
          ? {
              top: '50%',
              left: 0,
              height: $lineHeight,
              transform: 'translateY(-50%)',
              width: showProgress ? '100%' : '0%',
            }
          : {
              left: '50%',
              top: 0,
              width: $lineHeight,
              transform: 'translateX(-50%)',
              height: showProgress ? '100%' : '0%',
            }),
        background: connectorColor,
        borderRadius: $lineHeight / 2,
        transition: 'all 0.4s ease-out',
        boxShadow: showProgress ? `0 0 8px ${connectorColor}40` : 'none',
      },
    };
  },
);

const StyledStepWrapper = styled(
  Box,
  transientOptions,
)<{
  $orientation: 'horizontal' | 'vertical';
}>(({ $orientation }) => ({
  display: 'flex',
  flexDirection: $orientation === 'horizontal' ? 'column' : 'row',
  alignItems: $orientation === 'horizontal' ? 'center' : 'flex-start',
  gap: $orientation === 'horizontal' ? 8 : 12,
  minWidth: $orientation === 'horizontal' ? 70 : undefined,
  textAlign: $orientation === 'horizontal' ? 'center' : 'left',
}));

const StyledContainer = styled(
  Box,
  transientOptions,
)<{
  $orientation: 'horizontal' | 'vertical';
}>(({ $orientation }) => ({
  display: 'flex',
  flexDirection: $orientation === 'horizontal' ? 'row' : 'column',
  alignItems: $orientation === 'horizontal' ? 'flex-start' : 'flex-start',
  width: '100%',
  // No gap for vertical - connector handles spacing
  gap: $orientation === 'vertical' ? 0 : undefined,
}));

// ============================================================================
// Check Icon Component
// ============================================================================

const CheckIcon: React.FC<{ size: number }> = ({ size }) => (
  <svg
    width={size * 0.5}
    height={size * 0.5}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export const StepIndicator = forwardRef<HTMLDivElement, StepIndicatorProps>(
  (
    {
      steps,
      currentStep,
      onStepClick,
      clickable = true,
      neonAccent = 'cyan',
      size = 'medium',
      orientation = 'horizontal',
      useStatusMode = false,
      className,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const config = getSizeConfig(size);

    const { t } = useI18n();

    const handleStepClick = (index: number) => {
      if (clickable && onStepClick) {
        onStepClick(index);
      }
    };

    return (
      <StyledContainer ref={ref} $orientation={orientation} className={className}>
        {steps.map((step, index) => {
          // In status mode, use step.status; otherwise use position-based
          const stepStatus = useStatusMode ? step.status : undefined;
          const isActive = useStatusMode ? stepStatus === StepStatus.CURRENT : index === currentStep;
          const isCompleted = useStatusMode ? stepStatus === StepStatus.COMPLETED : index < currentStep;
          const isFirst = index === 0;
          const isLast = index === steps.length - 1;

          // Determine title styling based on status
          const getTitleColor = () => {
            if (useStatusMode && stepStatus) {
              if (stepStatus === StepStatus.ERROR) return STATUS_COLORS[StepStatus.ERROR];
              if (stepStatus === StepStatus.CURRENT) return theme.palette.primary.main;
              if (stepStatus === StepStatus.COMPLETED) return 'text.primary';
              return 'text.secondary';
            }
            return isActive || isCompleted ? 'text.primary' : 'text.secondary';
          };

          // Vertical layout: circle column on left, text on right
          if (orientation === 'vertical') {
            return (
              <Box
                key={step.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                }}
              >
                {/* Circle + Connector column */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mr: 2,
                  }}
                >
                  <StyledStepCircle
                    $isActive={isActive}
                    $isCompleted={isCompleted}
                    $isClickable={clickable && !!onStepClick}
                    $size={config.circle}
                    $neonAccent={neonAccent}
                    $isDark={isDark}
                    $status={stepStatus}
                    $useStatusMode={useStatusMode}
                    $isFirst={isFirst}
                    $isLast={isLast}
                    $orientation={orientation}
                    $lineWidth={config.lineHeight}
                    onClick={() => handleStepClick(index)}
                    whileHover={clickable && onStepClick ? { scale: 1.1 } : undefined}
                    whileTap={clickable && onStepClick ? { scale: 0.95 } : undefined}
                    style={{ fontSize: config.fontSize }}
                  >
                    {isCompleted && !useStatusMode ? <CheckIcon size={config.circle} /> : index + 1}
                  </StyledStepCircle>
                  {/* Vertical connector line */}
                  {!isLast && (
                    <Box
                      sx={{
                        flex: 1,
                        width: config.lineHeight,
                        minHeight: 16,
                        my: 0.5,
                        borderRadius: config.lineHeight / 2,
                        background: (() => {
                          if (useStatusMode && stepStatus === StepStatus.COMPLETED)
                            return STATUS_COLORS[StepStatus.COMPLETED];
                          if (useStatusMode && stepStatus === StepStatus.CURRENT) return theme.palette.primary.main;
                          if (isCompleted)
                            return neonAccent === 'primary' ? theme.palette.primary.main : neonAccents[neonAccent].base;
                          return isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
                        })(),
                        transition: 'background 0.3s ease',
                      }}
                    />
                  )}
                </Box>
                {/* Text column */}
                <Box
                  role={clickable && onStepClick ? 'button' : undefined}
                  tabIndex={clickable && onStepClick ? 0 : undefined}
                  onClick={() => handleStepClick(index)}
                  onKeyDown={
                    clickable && onStepClick
                      ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleStepClick(index);
                          }
                        }
                      : undefined
                  }
                  sx={{
                    flex: 1,
                    pb: isLast ? 0 : 3,
                    pt: 0.5,
                    cursor: clickable && onStepClick ? 'pointer' : 'default',
                    borderRadius: 1,
                    '&:hover': clickable && onStepClick ? { bgcolor: 'action.hover' } : undefined,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: config.titleSize,
                      fontWeight:
                        isActive || isCompleted || (useStatusMode && stepStatus === StepStatus.ERROR) ? 600 : 500,
                      color: getTitleColor(),
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                  >
                    {step.title}
                    {step.optional && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: config.descSize,
                          color: 'text.disabled',
                          fontWeight: 400,
                          ml: 0.5,
                        }}
                      >
                        ({t('common.optional')})
                      </Typography>
                    )}
                  </Typography>
                  {step.description && (
                    <Typography
                      sx={{
                        fontSize: config.descSize,
                        color: 'text.secondary',
                        lineHeight: 1.5,
                        maxWidth: 200,
                      }}
                    >
                      {step.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }

          // Horizontal layout (original)
          return (
            <Box
              key={step.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                flex: !isLast ? 1 : undefined,
              }}
            >
              <StyledStepWrapper $orientation={orientation}>
                <StyledStepCircle
                  $isActive={isActive}
                  $isCompleted={isCompleted}
                  $isClickable={clickable && !!onStepClick}
                  $size={config.circle}
                  $neonAccent={neonAccent}
                  $isDark={isDark}
                  $status={stepStatus}
                  $useStatusMode={useStatusMode}
                  $isFirst={isFirst}
                  $isLast={isLast}
                  $orientation={orientation}
                  $lineWidth={config.lineHeight}
                  onClick={() => handleStepClick(index)}
                  whileHover={clickable && onStepClick ? { scale: 1.1 } : undefined}
                  whileTap={clickable && onStepClick ? { scale: 0.95 } : undefined}
                  style={{ fontSize: config.fontSize }}
                >
                  {isCompleted && !useStatusMode ? <CheckIcon size={config.circle} /> : index + 1}
                </StyledStepCircle>

                <Box sx={{ mt: 1 }}>
                  <Typography
                    sx={{
                      fontSize: config.titleSize,
                      fontWeight:
                        isActive || isCompleted || (useStatusMode && stepStatus === StepStatus.ERROR) ? 600 : 500,
                      color: getTitleColor(),
                      lineHeight: 1.3,
                    }}
                  >
                    {step.title}
                    {step.optional && (
                      <Typography
                        component="span"
                        sx={{
                          fontSize: config.descSize,
                          color: 'text.disabled',
                          fontWeight: 400,
                          ml: 0.5,
                        }}
                      >
                        ({t('common.optional')})
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </StyledStepWrapper>

              {!isLast && (
                <StyledConnector
                  $isCompleted={isCompleted}
                  $neonAccent={neonAccent}
                  $isDark={isDark}
                  $orientation={orientation}
                  $lineHeight={config.lineHeight}
                  $circleSize={config.circle}
                  $useStatusMode={useStatusMode}
                  $fromStatus={stepStatus}
                />
              )}
            </Box>
          );
        })}
      </StyledContainer>
    );
  },
);

StepIndicator.displayName = 'StepIndicator';

export default StepIndicator;
